import { useState } from 'react';
import './App.css';

const CATEGORY_COLORS = {
  Productivity: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', icon: '🔧' },
  Utilities: { bg: 'rgba(16, 185, 129, 0.15)', color: '#34d399', icon: '⚙️' },
};

const SHOWCASE_APPS = [
  {
    id: 'pomodoro-timer',
    name: 'Pomodoro Timer',
    template: 'Menu Bar App',
    category: 'Productivity',
    description: 'A menu bar Pomodoro timer with focus/break cycles, progress tracking, keyboard shortcuts, and configurable durations. Lives in your menu bar — always one click away.',
    sizeMB: 0.2,
    fileCount: 4,
    lineCount: 163,
    features: ['Menu bar icon with popover UI', '25-min focus / 5-min break cycles', 'Visual progress bar', 'Completed session counter', 'Configurable durations in Settings'],
    frameworks: ['SwiftUI', 'AppKit'],
    files: {
      'PomodoroTimerApp.swift': 40,
      'MenuBarContentView.swift': 95,
      'SettingsView.swift': 17,
      'Package.swift': 11,
    },
  },
  {
    id: 'quick-note',
    name: 'QuickNote',
    template: 'SwiftUI App',
    category: 'Productivity',
    description: 'A minimal note-taking app with sidebar navigation, full-text search, monospaced editor, and local JSON persistence. Clean, fast, and private — all data stays on your Mac.',
    sizeMB: 0.3,
    fileCount: 4,
    lineCount: 161,
    features: ['Sidebar with note list', 'Full-text search', 'Monospaced text editor', 'Local persistence to Application Support', 'Create and delete via toolbar'],
    frameworks: ['SwiftUI', 'Foundation'],
    files: {
      'QuickNoteApp.swift': 10,
      'ContentView.swift': 110,
      'NoteEditorView.swift': 30,
      'Package.swift': 11,
    },
  },
  {
    id: 'system-dashboard',
    name: 'System Dashboard',
    template: 'SwiftUI App',
    category: 'Utilities',
    description: 'A real-time system monitor showing CPU, memory, and disk usage with circular gauges. Displays top processes by CPU and auto-refreshes every 3 seconds.',
    sizeMB: 0.3,
    fileCount: 3,
    lineCount: 201,
    features: ['Circular gauges for CPU, Memory, Disk', 'Live top-8 process list', '3-second auto-refresh', 'Hostname and uptime display', 'Color-coded usage levels'],
    frameworks: ['SwiftUI', 'Foundation'],
    files: {
      'SystemDashboardApp.swift': 10,
      'DashboardView.swift': 180,
      'Package.swift': 11,
    },
  },
  {
    id: 'unit-converter',
    name: 'Unit Converter',
    template: 'CLI Tool',
    category: 'Utilities',
    description: 'A command-line unit converter supporting 14 conversions across length, weight, and temperature. Single-file Swift script — compile and run in seconds.',
    sizeMB: 0.1,
    fileCount: 1,
    lineCount: 55,
    features: ['14 unit conversions', 'Length: km/miles, m/feet, cm/inches', 'Weight: kg/lbs, g/oz', 'Temperature: C/F, C/K', 'Formatted console output'],
    frameworks: ['Foundation'],
    files: {
      'main.swift': 55,
    },
  },
];

function MacOSAppHubPage() {
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(SHOWCASE_APPS.map((a) => a.category))];
  const filtered =
    selectedCategory === 'All'
      ? SHOWCASE_APPS
      : SHOWCASE_APPS.filter((a) => a.category === selectedCategory);

  return (
    <div className="mp-page" style={{ background: '#0f172a', color: '#f8fafc' }}>
      {/* Hero */}
      <div className="mp-hero" style={{ maxWidth: 700 }}>
        <h1
          className="fade-in-up"
          style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            marginBottom: 12,
            color: '#f8fafc',
            lineHeight: 1.1,
          }}
        >
          macOS App Hub
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#94a3b8', lineHeight: 1.6, marginBottom: 24 }}>
          Native macOS apps built by AI&nbsp;Wizard. Every app is open&#8209;source, security&#8209;scanned,
          and compiled into a real <code style={{ color: '#60a5fa', background: 'rgba(59,130,246,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: '0.95rem' }}>.app</code> bundle
          you can download and run.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <span className="trust-badge trust-verified" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
            ✓ Open Source
          </span>
          <span className="trust-badge trust-official" style={{ fontSize: '0.75rem', padding: '4px 10px' }}>
            ✓ Security Scanned
          </span>
          <span className="trust-badge" style={{ fontSize: '0.75rem', padding: '4px 10px', background: 'rgba(249,115,22,0.2)', color: '#fb923c' }}>
            ✓ AI Generated
          </span>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        className="mp-filter-bar"
        style={{ maxWidth: 900, margin: '0 auto 28px', justifyContent: 'center' }}
      >
        {categories.map((cat) => (
          <button
            key={cat}
            className={`mp-filter-tab ${selectedCategory === cat ? 'mp-filter-active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* App Grid */}
      <div className="mp-grid" style={{ maxWidth: 900, margin: '0 auto' }}>
        {filtered.map((app) => {
          const catStyle = CATEGORY_COLORS[app.category] || CATEGORY_COLORS.Utilities;
          const isSelected = selectedApp?.id === app.id;

          return (
            <article
              key={app.id}
              className="mp-card"
              onClick={() => setSelectedApp(isSelected ? null : app)}
              style={{
                cursor: 'pointer',
                borderColor: isSelected ? '#3b82f6' : undefined,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className="mp-card-header" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    className="mp-card-icon-bg"
                    style={{ background: catStyle.bg, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12, fontSize: '1.3rem' }}
                  >
                    {app.category === 'Productivity' ? '🛠' : '⚡'}
                  </div>
                  <div>
                    <h3 className="mp-card-title" style={{ marginBottom: 2 }}>{app.name}</h3>
                    <span
                      style={{
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: catStyle.color,
                        fontWeight: 600,
                      }}
                    >
                      {app.template}
                    </span>
                  </div>
                </div>
                <span className="trust-badge trust-verified" style={{ fontSize: '0.65rem' }}>
                  ✓ Scanned
                </span>
              </div>

              <p className="mp-card-summary" style={{ flex: 1 }}>
                {app.description}
              </p>

              <div className="mp-card-footer">
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {app.fileCount} files · {app.lineCount} lines · {app.sizeMB}MB
                </span>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>MIT</span>
              </div>
            </article>
          );
        })}
      </div>

      {/* Detail Panel */}
      {selectedApp && (
        <div
          className="mp-card fade-in-up"
          style={{
            maxWidth: 900,
            margin: '24px auto 0',
            padding: 28,
            borderColor: '#3b82f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
            <div
              style={{
                background: CATEGORY_COLORS[selectedApp.category]?.bg,
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 14,
                fontSize: '1.8rem',
                flexShrink: 0,
              }}
            >
              {selectedApp.category === 'Productivity' ? '🛠' : '⚡'}
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f8fafc', margin: 0 }}>
                {selectedApp.name}
              </h2>
              <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: '0.88rem' }}>
                by AI Wizard · v1.0 · MIT License · {selectedApp.template}
              </p>
            </div>
          </div>

          {/* Features */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: 10 }}>
              Features
            </h4>
            <ul style={{ color: '#e2e8f0', fontSize: '0.9rem', lineHeight: 1.9, paddingLeft: 20, margin: 0 }}>
              {selectedApp.features.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>

          {/* Frameworks */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: 10 }}>
              Frameworks
            </h4>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedApp.frameworks.map((fw) => (
                <span
                  key={fw}
                  style={{
                    fontSize: '0.78rem',
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#60a5fa',
                    fontWeight: 500,
                  }}
                >
                  {fw}
                </span>
              ))}
            </div>
          </div>

          {/* Source Files */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: 10 }}>
              Source Files
            </h4>
            <div
              style={{
                background: 'rgba(15, 23, 42, 0.6)',
                borderRadius: 10,
                border: '1px solid #1e293b',
                overflow: 'hidden',
              }}
            >
              {Object.entries(selectedApp.files).map(([name, lines], i, arr) => (
                <div
                  key={name}
                  style={{
                    padding: '10px 16px',
                    borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '0.82rem', color: '#60a5fa' }}>
                    {name}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#475569' }}>{lines} lines</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a
              href="https://mchatai.com/macos-app"
              className="mp-cta-button"
              style={{ textDecoration: 'none' }}
            >
              Get mChatAI+ to build your own
            </a>
            <button
              className="mp-cta-button"
              style={{ background: '#334155' }}
              onClick={() => {
                const el = document.createElement('a');
                el.href = `data:text/plain;charset=utf-8,${encodeURIComponent(
                  '// Download the full source from the mChatAI+ macOS App Hub.\n// Open mChatAI+, go to AI Wizard, and say:\n//   "make me a ' + selectedApp.name.toLowerCase() + '"\n// The AI will generate, compile, and package it for you.'
                )}`;
                el.download = `${selectedApp.name}-recipe.txt`;
                el.click();
              }}
            >
              Download Recipe
            </button>
          </div>

          {/* Security Report */}
          <div
            style={{
              marginTop: 20,
              padding: '14px 16px',
              background: 'rgba(16, 185, 129, 0.08)',
              borderRadius: 10,
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ color: '#34d399', fontWeight: 700, fontSize: '0.85rem' }}>
                Security Scan: PASSED
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: '0.75rem', color: '#94a3b8', flexWrap: 'wrap' }}>
              <span>✓ Static analysis</span>
              <span>✓ No dangerous APIs</span>
              <span>✓ Compile verified</span>
              <span>✓ No network access</span>
              <span>✓ Sandbox safe</span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div
        style={{
          maxWidth: 600,
          margin: '48px auto 0',
          textAlign: 'center',
          padding: '32px 24px',
        }}
      >
        <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f8fafc', marginBottom: 8 }}>
          Build your own macOS apps with AI
        </h3>
        <p style={{ color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 20 }}>
          Describe any app in plain English. AI Wizard generates the Swift code,
          compiles it, and packages a real <code style={{ color: '#60a5fa' }}>.app</code> bundle you can run on your Mac.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a href="https://mchatai.com/macos-app" className="mp-cta-button" style={{ textDecoration: 'none' }}>
            Download mChatAI+
          </a>
          <a href="https://mchatai.com/docs" className="mp-cta-button" style={{ textDecoration: 'none', background: '#334155' }}>
            Read the Docs
          </a>
        </div>
      </div>
    </div>
  );
}

export default MacOSAppHubPage;
